/**
 * SearchPanel.tsx — Ex-US Professionals V3
 * Search fields row: First Name, Last Name, City, Country, State/Province,
 * Postal Code, Prof ID, and action buttons.
 * Uses react-hook-form 7 — fields are wired with {...register(name)}.
 *
 * Country-select strategy:
 *   The country <select> is controlled by localCountry React state (not by RHF
 *   watch or a controlled value fed back from the parent).  On every change we
 *   call setValue('countryCode', val) so getValues() / form submission always
 *   sees the current value.  This sidesteps a watch re-render race (seen on RHF v6)
 *   that caused the dropdown to snap back to "-- Select --".
 */
import React from 'react';
import type { UseFormRegister, UseFormSetValue } from 'react-hook-form';
import styles from '../ExUsProfessionalsV3.module.css';
import exusLookup from '../../../lookup/exus.json';
import countrystatelookup from '../../../lookup/countrystatelookup.json';

export interface SearchFormValues {
    firstName:   string;
    lastName:    string;
    city:        string;
    countryCode: string;
    state:       string;
    postalCode:  string;
    profId:      string;
    degree:      string;
    specialty:   string;
    universe:    string;
}

interface Props {
    register:   UseFormRegister<SearchFormValues>;
    setValue:   UseFormSetValue<SearchFormValues>;
    onSearch:   () => void;
    onClearAll: () => void;
    isLoading:  boolean;
}

// Country options built once at module load from exus.json searchParams
const countryParam = (exusLookup as {
    searchParams: Array<{ column: string; type: string; options?: Record<string, string> }>;
}).searchParams.find(p => p.column === 'COUNTRY_CODE');

const countryOptions: Array<{ value: string; label: string }> = countryParam?.options
    ? Object.entries(countryParam.options).map(([value, label]) => ({ value, label }))
    : [];

export default function SearchPanel({ register, setValue, onSearch, onClearAll, isLoading }: Props) {
    // localCountry is the single source of truth for the country select value.
    // It is React state owned by this component, so it updates synchronously on
    // selection — no RHF watch / re-render cycle involved.
    const [localCountry, setLocalCountry] = React.useState('');

    const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setLocalCountry(val);           // update controlled select immediately
        setValue('countryCode', val);   // keep RHF store in sync for getValues()
        setValue('state', '');          // clear dependent state field
    };

    // Reset local state when parent resets the whole form (CLEAR ALL)
    const handleClearAll = () => {
        setLocalCountry('');
        onClearAll();                   // parent calls reset(DEFAULT_VALUES)
    };

    // State options filtered by selected country (empty until a country is chosen)
    const stateOptions: Array<{ value: string; label: string }> = React.useMemo(() => {
        if (!localCountry) return [];
        const lookup = countrystatelookup as Record<string, string>;
        return Object.entries(lookup)
            .filter(([key]) => key.substring(0, 2).toLowerCase() === localCountry.toLowerCase())
            // DB ML.STATE stores only the region code (e.g. "AB"), not the full ISO key ("CA-AB")
            .map(([key, label]) => ({ value: key.substring(key.indexOf('-') + 1), label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [localCountry]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') onSearch();
    };

    return (
        <div className={styles.searchPanel} onKeyDown={handleKeyDown}>

            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>First Name</label>
                <input
                    className={styles.fieldInput}
                    {...register('firstName')}
                    autoComplete="off"
                    data-testid="field-firstName"
                />
            </div>

            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>Last Name</label>
                <input
                    className={styles.fieldInput}
                    {...register('lastName')}
                    autoComplete="off"
                    data-testid="field-lastName"
                />
            </div>

            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>City</label>
                <input
                    className={styles.fieldInput}
                    {...register('city')}
                    autoComplete="off"
                    data-testid="field-city"
                />
            </div>

            {/* Country — controlled by localCountry state, synced to RHF via setValue */}
            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>Country</label>
                <select
                    className={styles.fieldSelect}
                    value={localCountry}
                    onChange={handleCountryChange}
                    data-testid="field-countryCode"
                    style={{ minWidth: 160 }}
                >
                    <option value="">— Select —</option>
                    {countryOptions.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>

            {/* State — populated only after a country is selected */}
            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>State / Province</label>
                <select
                    className={styles.fieldSelect}
                    {...register('state')}
                    data-testid="field-state"
                >
                    <option value="">— Select —</option>
                    {stateOptions.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>

            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>Postal Code</label>
                <input
                    className={styles.fieldInput}
                    style={{ minWidth: 100 }}
                    {...register('postalCode')}
                    autoComplete="off"
                    data-testid="field-postalCode"
                />
            </div>

            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>Prof ID</label>
                <input
                    className={styles.fieldInput}
                    style={{ minWidth: 90 }}
                    {...register('profId')}
                    autoComplete="off"
                    data-testid="field-profId"
                />
            </div>

            <div className={styles.searchActions}>
                <button
                    type="button"
                    onClick={onSearch}
                    disabled={isLoading}
                    data-testid="btn-search"
                    style={{
                        background: '#fff',
                        color: '#3d3e8e',
                        border: '1px solid #fff',
                        borderRadius: 3,
                        padding: '6px 18px',
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        opacity: isLoading ? 0.6 : 1,
                    }}
                >
                    🔍 SEARCH
                </button>
                <button
                    type="button"
                    onClick={handleClearAll}
                    disabled={isLoading}
                    data-testid="btn-clearAll"
                    style={{
                        background: 'transparent',
                        color: '#fff',
                        border: '1px solid #fff',
                        borderRadius: 3,
                        padding: '6px 14px',
                        fontSize: 13,
                        cursor: 'pointer',
                    }}
                >
                    CLEAR ALL
                </button>
            </div>

        </div>
    );
}
